import { useEffect, useMemo, useState } from "react";
import { Plus, CheckCircle2, Trash2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatShort, formatApiError } from "../lib/format";
import TaskDialog from "../components/TaskDialog";

export default function Agenda() {
  const [tasks, setTasks] = useState([]);
  const [dashboardEvents, setDashboardEvents] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [filter, setFilter] = useState("todos");

  const load = async () => {
    try {
      const [t, d] = await Promise.all([api.get("/tasks"), api.get("/dashboard")]);
      setTasks(t.data);
      setDashboardEvents(d.data.events);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const events = useMemo(() => {
    // Merge dashboard events with tasks not yet in events
    const map = new Map();
    dashboardEvents.forEach(e => map.set(e.id, e));
    tasks.forEach(t => {
      const key = `t_${t.id}`;
      if (!map.has(key)) {
        map.set(key, {
          id: key, tid: t.id, title: t.title, client: t.client_name || "—",
          date: t.due_date, type: t.kind || "Tarefa", detail: t.description, done: t.done,
        });
      } else {
        map.get(key).done = t.done;
        map.get(key).tid = t.id;
      }
    });
    return Array.from(map.values()).filter(e => {
      if (filter === "urgente") return e.type === "Urgente";
      if (filter === "processo") return e.type === "Processo";
      if (filter === "tarefa") return e.type === "Tarefa" || e.type === "Ligação" || e.type === "Documento";
      return true;
    }).sort((a,b) => (a.date||"").localeCompare(b.date||""));
  }, [dashboardEvents, tasks, filter]);

  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  const firstDay = new Date(cursor.y, cursor.m, 1);
  const daysInMonth = new Date(cursor.y, cursor.m+1, 0).getDate();
  const startWeekday = firstDay.getDay(); // 0..6

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const today = new Date();
  const iso = (d) => `${cursor.y}-${String(cursor.m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;

  const toggleDone = async (task) => {
    if (!task.tid) return;
    try { await api.patch(`/tasks/${task.tid}`, { done: !task.done }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeTask = async (task) => {
    if (!task.tid) return;
    if (!confirm("Excluir esta tarefa?")) return;
    try { await api.delete(`/tasks/${task.tid}`); toast.success("Tarefa removida"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="page-heading">
        <div>
          <span className="eyebrow">CALENDÁRIO CONSOLIDADO</span>
          <h1>Agenda</h1>
          <p>Vencimentos, processos e tarefas em uma única linha do tempo.</p>
        </div>
        <button data-testid="new-task-button" className="primary-button" onClick={()=>setDialog(true)}><Plus size={17}/> Nova tarefa</button>
      </div>

      <section className="panel full-panel agenda-page">
        <div className="toolbar agenda-toolbar">
          <div className="agenda-nav">
            <button data-testid="agenda-prev" onClick={()=>setCursor(c=>({y: c.m===0?c.y-1:c.y, m:(c.m+11)%12}))}>‹</button>
            <b className="agenda-month">{monthLabel}</b>
            <button data-testid="agenda-next" onClick={()=>setCursor(c=>({y: c.m===11?c.y+1:c.y, m:(c.m+1)%12}))}>›</button>
          </div>
          <div className="filter-buttons">
            {[["todos","Todos"],["urgente","Urgentes"],["processo","Processos"],["tarefa","Tarefas"]].map(([k,l])=>(
              <button key={k} data-testid={`agenda-filter-${k}`} className={`filter ${filter===k?"active":""}`} onClick={()=>setFilter(k)}>{l}</button>
            ))}
          </div>
        </div>

        <div className="month-grid">
          {["DOM","SEG","TER","QUA","QUI","SEX","SÁB"].map(w => <div key={w} className="month-weekday">{w}</div>)}
          {cells.map((d,i) => {
            if (!d) return <div className="month-day empty-day" key={`e${i}`}/>;
            const dayIso = iso(d);
            const isToday = today.getFullYear()===cursor.y && today.getMonth()===cursor.m && today.getDate()===d;
            const dayEvents = events.filter(e => e.date === dayIso);
            return (
              <div key={dayIso} className={`month-day ${isToday?"selected":""}`}>
                <small>{d}</small>
                {dayEvents.slice(0,3).map(e => (
                  <span key={e.id} className={e.type === "Urgente" ? "urgent" : e.type === "Processo" ? "processo" : "normal"} title={e.title}>{e.title}</span>
                ))}
                {dayEvents.length > 3 && <span className="normal">+{dayEvents.length - 3}</span>}
              </div>
            );
          })}
        </div>

        <div className="agenda-list">
          <div className="panel-head"><span className="eyebrow">EVENTOS PRÓXIMOS</span></div>
          {events.slice(0, 20).map(e => (
            <div className="event-row" data-testid={`agenda-event-${e.id}`} key={e.id}>
              <div className={`event-dot ${(e.type||"").toLowerCase()}`}/>
              <div className="event-info">
                <b>{e.title}</b>
                <span>{e.client} · {e.detail || "—"}</span>
              </div>
              <time>{formatShort(e.date)}</time>
              {e.tid && (
                <div className="row-actions">
                  <button data-testid={`toggle-done-${e.tid}`} className={`icon-mini ${e.done?"success":""}`} onClick={()=>toggleDone(e)}><CheckCircle2 size={14}/></button>
                  <button data-testid={`delete-task-${e.tid}`} className="icon-mini danger" onClick={()=>removeTask(e)}><Trash2 size={14}/></button>
                </div>
              )}
            </div>
          ))}
          {!events.length && <div className="event-empty"><CalendarDays size={22}/> Nenhum evento neste filtro.</div>}
        </div>
      </section>

      <TaskDialog open={dialog} onClose={()=>setDialog(false)} onSaved={load}/>
    </>
  );
}
